import { useEffect, useState } from "react";
import { useRegistration } from "@/context/RegistrationContext";
import { DOCUMENT_TYPES, POLICY_ITEMS, registrationIdLabel } from "@/lib/constants";
import { ArrowLeft, PencilLine, Send, FileText, CheckCircle2, AlertTriangle, Clock, MapPin, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { submitRegistration, previewSlot, getSlotOptions } from "@/lib/api";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const Row = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-2">
    <div className="text-xs uppercase tracking-wider text-[#9CA3AF] sm:w-48 shrink-0 font-semibold">
      {label}
    </div>
    <div className="text-sm text-[#111] font-medium break-words">{value || "—"}</div>
  </div>
);

export default function ReviewStep({ onBack, onEdit }) {
  const nav = useNavigate();
  const { state, update } = useRegistration();
  const c = state.candidate;
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null); // { file, docLabel }
  const [upcomingSlot, setUpcomingSlot] = useState(null);
  // Token-type slot groups: candidate picks their own date/time window
  // instead of being auto-assigned one — { bookable, slots: [...] }.
  const [slotOptions, setSlotOptions] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    if (!state.applicationNumber) return;
    (async () => {
      const opts = await getSlotOptions(state.applicationNumber);
      if (opts?.bookable) {
        setSlotOptions(opts);
        return;
      }
      // Not a token-bookable group (seat-type, or no group configured yet) —
      // keep the original auto-assign preview, unchanged.
      const s = await previewSlot(state.applicationNumber);
      if (s) setUpcomingSlot(s);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.applicationNumber]);

  // Walkin groups: no booking at all — just a date-range/time-window/venue
  // to visit within, token number issued at check-in. Distinct from the
  // seat-preview (FCFS) and token-picker (bookable) cases below.
  const isWalkin = !slotOptions?.bookable && upcomingSlot?.mode === "walkin";

  // Windows grouped by date, in the order the backend already returns them
  // (date then hour ascending) — no need to re-sort.
  const slotsByDate = [];
  if (slotOptions?.bookable) {
    for (const w of slotOptions.slots) {
      const lastGroup = slotsByDate[slotsByDate.length - 1];
      if (lastGroup && lastGroup.dateISO === w.dateISO) lastGroup.windows.push(w);
      else slotsByDate.push({ dateISO: w.dateISO, dateLabel: w.dateLabel, windows: [w] });
    }
  }

  // Pending items summary — now blocking (all mandatory)
  // Final submission requires all forms & policies to be actually SIGNED —
  // not just sent for digital signature. The Policies step itself only
  // requires "sent" to move past it; this is the stricter final gate.
  // Policies marked `optional` don't have a Leegality workflow configured
  // yet — don't block submission on them.
  const skipPolicies = !!state.skipPolicies;
  const missingPolicies = skipPolicies
    ? []
    : POLICY_ITEMS.filter((p) => !p.optional && state.policies?.[p.id]?.signStatus !== "signed");
  const requiredDocs = DOCUMENT_TYPES.filter((d) => d.required);
  const missingDocs = requiredDocs.filter((d) => !(state.documents?.[d.id] || []).length);
  // Contact & Parent Details (step 1) — emails aren't OTP-verified (delivery
  // isn't reliable), just checked for presence/valid format like any other field.
  const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());
  const parent2Touched = !!(c.parent2Name || c.parent2Phone || c.parent2Relation || c.parent2Email);
  const missingContactFields = [];
  if (!emailValid(c.email)) missingContactFields.push("Your Email");
  if (!c.parent1Name) missingContactFields.push("Parent 1 Name");
  if (!c.parent1Phone) missingContactFields.push("Parent 1 Phone");
  if (!c.parent1Relation) missingContactFields.push("Parent 1 Relation");
  if (!emailValid(c.parent1Email)) missingContactFields.push("Parent 1 Email");
  if (parent2Touched) {
    if (!c.parent2Name) missingContactFields.push("Parent 2 Name");
    if (!c.parent2Phone) missingContactFields.push("Parent 2 Phone");
    if (!c.parent2Relation) missingContactFields.push("Parent 2 Relation");
    if (!emailValid(c.parent2Email)) missingContactFields.push("Parent 2 Email");
  }

  const missingCandidateFields = [];
  if (!c.name || c.name.trim().length < 3) missingCandidateFields.push("Full Name");
  if (!c.photo) missingCandidateFields.push("Photograph");
  if (!c.nationality) missingCandidateFields.push("Nationality");
  if (!c.affidavitRef) missingCandidateFields.push("Anti-Ragging Ref. No.");
  if (!c.dateOfBirth) missingCandidateFields.push("Date of Birth");
  if (!c.educationBoard) missingCandidateFields.push("Qualifying Board (12th)");
  if (!c.gender) missingCandidateFields.push("Gender");
  if (!c.bloodGroup) missingCandidateFields.push("Blood Group");
  if (!c.address) missingCandidateFields.push("Address");
  if (!c.city) missingCandidateFields.push("City");
  if (!c.state) missingCandidateFields.push("State");
  if (!c.pincode) missingCandidateFields.push("PIN Code");
  if (!c.emergencyContactName) missingCandidateFields.push("Emergency Contact Name");
  if (!c.emergencyContactPhone) missingCandidateFields.push("Emergency Phone");
  if (!c.emergencyContactRelation) missingCandidateFields.push("Emergency Relationship");

  const slotSelectionMissing = !!slotOptions?.bookable && !selectedSlot;

  const isBlocked =
    missingContactFields.length + missingCandidateFields.length + missingDocs.length + missingPolicies.length > 0 ||
    !state.undertaking?.confirmed ||
    slotSelectionMissing;

  const submit = async () => {
    if (isBlocked) {
      toast.error(slotSelectionMissing ? "Please select a reporting slot before submitting" : "Please complete all required items before submitting");
      return;
    }
    setSubmitting(true);
    // ACK is generated authoritatively by the backend (MU-REG-{100+count}).
    // Never send one from the client — otherwise it overrides the server sequence.
    const payload = slotOptions?.bookable && selectedSlot
      ? { chosen_date: selectedSlot.dateISO, chosen_start_hour: selectedSlot.startHour }
      : {};
    const res = await submitRegistration(state.applicationNumber, payload);
    if (!res.ok) {
      setSubmitting(false);
      if (res.status === 409) {
        // Someone else took the slot between selection and submit — refresh
        // availability so the picker reflects the current state.
        toast.error(res.data?.detail || "That slot just filled up — please choose another.");
        const opts = await getSlotOptions(state.applicationNumber);
        if (opts?.bookable) setSlotOptions(opts);
        setSelectedSlot(null);
      } else {
        toast.error(res.data?.detail || "Could not submit — please try again");
      }
      return;
    }
    const result = res.data;
    update({
      submitted: true,
      ackNumber: result.ack_number || "",
      submittedAt: result.submitted_at || new Date().toISOString(),
      assignedSlot: result.assigned_slot || null,
    });
    toast.success("Registration submitted successfully");
    nav("/success");
  };

  return (
    <div className="space-y-6">
      {/* Pending items */}
      {(missingPolicies.length + missingDocs.length + missingCandidateFields.length + missingContactFields.length) > 0 && (
        <div className="mu-card p-5 md:p-6 border-red-200 bg-red-50/60" data-testid="pending-items">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-red-700" />
            <h3 className="font-semibold text-[#111]">Pending items — submission blocked</h3>
          </div>
          <p className="text-xs text-[#6B7280] -mt-1 mb-3">
            All items below are required. Complete them from the corresponding step to enable submission.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            {missingContactFields.length > 0 && (
              <div>
                <div className="tiny-label mb-1">Contact & Parent details</div>
                <ul className="list-disc list-inside text-[#111] space-y-0.5">
                  {missingContactFields.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}
            {missingCandidateFields.length > 0 && (
              <div>
                <div className="tiny-label mb-1">Candidate details</div>
                <ul className="list-disc list-inside text-[#111] space-y-0.5">
                  {missingCandidateFields.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}
            {missingDocs.length > 0 && (
              <div>
                <div className="tiny-label mb-1">Documents</div>
                <ul className="list-disc list-inside text-[#111] space-y-0.5">
                  {missingDocs.map((d) => <li key={d.id}>{d.label}</li>)}
                </ul>
              </div>
            )}
            {missingPolicies.length > 0 && (
              <div>
                <div className="tiny-label mb-1">Forms & Policies</div>
                <ul className="list-disc list-inside text-[#111] space-y-0.5">
                  {missingPolicies.map((p) => <li key={p.id}>{p.label}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contact & Parent Details */}
      <div className="mu-card p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="tiny-label">Section 1</div>
            <h3 className="text-lg font-semibold text-[#111]">Contact & Parent Details</h3>
          </div>
          <button className="mu-btn-ghost text-sm" onClick={() => onEdit(1)} data-testid="btn-edit-contact">
            <PencilLine className="h-4 w-4" /> Edit
          </button>
        </div>
        <div className="divide-y divide-[#F3F4F6]">
          <Row label="Your Email" value={c.email} />
          <Row label="Parent 1" value={[c.parent1Name, c.parent1Relation].filter(Boolean).join(" · ")} />
          <Row label="Parent 1 Phone" value={c.parent1Phone} />
          <Row label="Parent 1 Email" value={c.parent1Email} />
          {parent2Touched && (
            <>
              <Row label="Parent 2" value={[c.parent2Name, c.parent2Relation].filter(Boolean).join(" · ")} />
              <Row label="Parent 2 Phone" value={c.parent2Phone} />
              <Row label="Parent 2 Email" value={c.parent2Email} />
            </>
          )}
        </div>
      </div>

      {/* Candidate Details */}
      <div className="mu-card p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="tiny-label">Section 2</div>
            <h3 className="text-lg font-semibold text-[#111]">Candidate Details</h3>
          </div>
          <button className="mu-btn-ghost text-sm" onClick={() => onEdit(2)} data-testid="btn-edit-candidate">
            <PencilLine className="h-4 w-4" /> Edit
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="h-28 w-28 rounded-xl overflow-hidden bg-[#F7F7F7] border border-[#E5E7EB]">
            {(c.photo?.file_url || c.photo?.dataUrl) ? (
              <img src={c.photo.file_url || c.photo.dataUrl} alt="Candidate" className="w-full h-full object-cover" />
            ) : (
              <div className="flex items-center justify-center h-full text-[#9CA3AF] text-xs">No photo</div>
            )}
          </div>
          <div className="divide-y divide-[#F3F4F6]">
            <Row label={registrationIdLabel(skipPolicies)} value={state.applicationNumber} />
            <Row label="Full Name" value={c.name} />
            <Row label="Mobile" value={state.mobile} />
            <Row label="Nationality" value={c.nationality} />
            <Row label="Anti-Ragging Ref." value={c.affidavitRef} />
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="mu-card p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="tiny-label">Section 3</div>
            <h3 className="text-lg font-semibold text-[#111]">Uploaded Documents</h3>
          </div>
          <button className="mu-btn-ghost text-sm" onClick={() => onEdit(3)} data-testid="btn-edit-documents">
            <PencilLine className="h-4 w-4" /> Edit
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {DOCUMENT_TYPES.map((d) => {
            const files = state.documents[d.id] || [];
            const has = files.length > 0;
            const firstFile = files[0];
            const firstFileSrc = firstFile?.file_url || firstFile?.dataUrl;
            const isImage =
              firstFileSrc?.startsWith("data:image") ||
              /\.(png|jpe?g|webp|gif)$/i.test(firstFile?.name || "");
            return (
              <div
                key={d.id}
                className="flex items-center justify-between p-3 rounded-xl border border-[#E5E7EB] bg-[#F7F7F7]/50"
                data-testid={`review-doc-${d.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    type="button"
                    onClick={() => has && setPreview({ file: firstFile, docLabel: d.label })}
                    className={`h-10 w-10 rounded-lg bg-white border border-[#E5E7EB] flex items-center justify-center overflow-hidden shrink-0 ${
                      has ? "hover:border-[#B60F2A] transition" : ""
                    }`}
                    aria-label={has ? "Preview file" : "No file"}
                    disabled={!has}
                    data-testid={`review-thumb-${d.id}`}
                  >
                    {has && isImage && firstFileSrc ? (
                      <img src={firstFileSrc} alt={firstFile.name} className="w-full h-full object-cover" />
                    ) : (
                      <FileText className={`h-4 w-4 ${has ? "text-[#B60F2A]" : "text-[#9CA3AF]"}`} />
                    )}
                  </button>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#111] truncate">{d.label}</div>
                    <div className="text-xs text-[#9CA3AF]">
                      {has ? `${files.length} file${files.length > 1 ? "s" : ""} uploaded` : "Not uploaded"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {has && (
                    <button
                      type="button"
                      onClick={() => setPreview({ file: firstFile, docLabel: d.label })}
                      className="p-2 rounded-lg text-[#6B7280] hover:text-[#B60F2A] hover:bg-white transition"
                      aria-label="Preview"
                      data-testid={`review-preview-${d.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  )}
                  {has ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 ring-1 ring-inset ring-green-200 px-2 py-1 rounded-full">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Ready
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-200 px-2 py-1 rounded-full">
                      Pending
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <FilePreviewDialog
        open={!!preview}
        onOpenChange={(v) => !v && setPreview(null)}
        file={preview?.file}
        docLabel={preview?.docLabel}
      />

      {/* Forms & Policies — skipped for employee/course registrants */}
      {!skipPolicies && (
      <div className="mu-card p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="tiny-label">Section 4</div>
            <h3 className="text-lg font-semibold text-[#111]">Forms &amp; Policies</h3>
          </div>
          <button className="mu-btn-ghost text-sm" onClick={() => onEdit(4)} data-testid="btn-edit-policies">
            <PencilLine className="h-4 w-4" /> Edit
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {POLICY_ITEMS.map((p) => {
            const signStatus = state.policies?.[p.id]?.signStatus;
            const isSigned = signStatus === "signed";
            const isPending = signStatus === "pending";
            return (
              <div
                key={p.id}
                className="flex items-center justify-between p-3 rounded-xl border border-[#E5E7EB] bg-[#F7F7F7]/50"
                data-testid={`review-policy-${p.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-lg bg-white border border-[#E5E7EB] flex items-center justify-center overflow-hidden shrink-0">
                    <FileText className={`h-4 w-4 ${isSigned ? "text-[#B60F2A]" : "text-[#9CA3AF]"}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#111] truncate">{p.label}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isSigned ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 ring-1 ring-inset ring-green-200 px-2 py-1 rounded-full">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {p.viewOnly ? "Read" : "Signed"}
                    </span>
                  ) : isPending ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 bg-orange-50 ring-1 ring-inset ring-orange-200 px-2 py-1 rounded-full">
                      Digital Signature Pending
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-200 px-2 py-1 rounded-full">
                      {p.viewOnly ? "Not Read" : "Pending"}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Undertaking */}
      <div className="mu-card p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="tiny-label">Section 5</div>
            <h3 className="text-lg font-semibold text-[#111]">Undertaking</h3>
          </div>
          <button className="mu-btn-ghost text-sm" onClick={() => onEdit(5)} data-testid="btn-edit-undertaking">
            <PencilLine className="h-4 w-4" /> Edit
          </button>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
          <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium text-[#111]">Declaration confirmed by {c.name}</div>
            <div className="text-xs text-[#6B7280] mt-0.5">
              Signed on{" "}
              {state.undertaking?.signedAt
                ? new Date(state.undertaking.signedAt).toLocaleString()
                : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Slot picker — token-type groups: candidate picks their own date/time window */}
      {slotOptions?.bookable && (
      <div className="mu-card p-5 md:p-6 border-[#FBE7EA] bg-[#FBE7EA]/30" data-testid="slot-picker">
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 text-[#B60F2A] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="tiny-label">Pick your reporting slot</div>
            <p className="text-sm text-[#111] mt-1">
              Your program doesn&rsquo;t pre-assign a seat — choose a reporting <span className="font-semibold">date and time window</span> below. You&rsquo;ll receive your token number when you check in at the venue.
            </p>
            <div className="space-y-4 mt-4">
              {slotsByDate.map((group) => (
                <div key={group.dateISO}>
                  <div className="text-xs font-semibold text-[#111] mb-2">{group.dateLabel}</div>
                  <div className="flex flex-wrap gap-2">
                    {group.windows.map((w) => {
                      const isSelected = selectedSlot?.dateISO === w.dateISO && selectedSlot?.startHour === w.startHour;
                      const isFull = w.status === "full";
                      const isFillingFast = w.status === "filling_fast";
                      return (
                        <button
                          key={`${w.dateISO}-${w.startHour}`}
                          type="button"
                          disabled={isFull}
                          onClick={() => setSelectedSlot(w)}
                          data-testid={`slot-option-${w.dateISO}-${w.startHour}`}
                          data-status={w.status}
                          className={`px-3 py-2 rounded-xl border text-xs font-medium transition text-left ${
                            isFull
                              ? "border-[#E5E7EB] bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed"
                              : isSelected
                              ? "border-[#B60F2A] bg-[#B60F2A] text-white"
                              : isFillingFast
                              ? "border-amber-300 bg-amber-50 text-amber-800 hover:border-[#B60F2A]"
                              : "border-[#E5E7EB] bg-white text-[#111] hover:border-[#B60F2A]"
                          }`}
                        >
                          <div>{w.timeLabel}</div>
                          <div className={`text-[10px] mt-0.5 ${isSelected ? "opacity-90" : "opacity-70"}`}>
                            {isFull ? "Full" : isFillingFast ? `Filling fast · ${w.remaining} left` : `${w.remaining} of ${w.capacity} left`}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 text-xs text-[#6B7280] mt-4">
              <MapPin className="h-3.5 w-3.5" /> Venue: {selectedSlot?.venue || slotOptions.slots[0]?.venue || "School of Management Building, Mahindra University"}
            </div>
            {!selectedSlot && (
              <div className="text-xs text-amber-700 mt-2">Please select a reporting slot to continue.</div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Slot preview — seat-type groups (or cohorts with no configured group):
          auto-assigned FCFS, not picked by the candidate. */}
      {!slotOptions?.bookable && upcomingSlot && !isWalkin && (
      <div className="mu-card p-5 md:p-6 border-[#FBE7EA] bg-[#FBE7EA]/30" data-testid="slot-preview">
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 text-[#B60F2A] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="tiny-label">Reporting slot preview</div>
            <p className="text-sm text-[#111] mt-1">
              Slots are allotted on a <span className="font-semibold">first-come, first-serve basis</span> — 1 hour each with a maximum of <span className="font-semibold">40 candidates per slot</span>, based on your submission time.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
              <div className="p-3 rounded-xl bg-white border border-[#E5E7EB]">
                <div className="tiny-label">Likely Date</div>
                <div className="text-sm font-semibold text-[#111] mt-1">{upcomingSlot.dateLabel}</div>
              </div>
              <div className="p-3 rounded-xl bg-white border border-[#E5E7EB]">
                <div className="tiny-label">Likely Time</div>
                <div className="text-sm font-semibold text-[#111] mt-1">{upcomingSlot.timeLabel}</div>
              </div>
              <div className="p-3 rounded-xl bg-white border border-[#E5E7EB]">
                <div className="tiny-label">Seat position</div>
                <div className="text-sm font-semibold text-[#111] mt-1">
                  #{upcomingSlot.seatInSlot}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#6B7280] mt-3">
              <MapPin className="h-3.5 w-3.5" /> Venue: {upcomingSlot.venue || "School of Management Building, Mahindra University"}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Walkin groups: no booking at all — just the range to show up within. */}
      {isWalkin && upcomingSlot && (
      <div className="mu-card p-5 md:p-6 border-[#FBE7EA] bg-[#FBE7EA]/30" data-testid="slot-preview-walkin">
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 text-[#B60F2A] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="tiny-label">Reporting window</div>
            <p className="text-sm text-[#111] mt-1">
              No slot booking needed — visit any day within this window. Your token number will be issued when you check in.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <div className="p-3 rounded-xl bg-white border border-[#E5E7EB]">
                <div className="tiny-label">Dates</div>
                <div className="text-sm font-semibold text-[#111] mt-1">{upcomingSlot.dateLabel}</div>
              </div>
              <div className="p-3 rounded-xl bg-white border border-[#E5E7EB]">
                <div className="tiny-label">Time</div>
                <div className="text-sm font-semibold text-[#111] mt-1">{upcomingSlot.timeLabel}</div>
              </div>
            </div>
            {upcomingSlot.excludedDatesLabel && (
              <p className="text-xs text-amber-700 mt-3">Closed on: {upcomingSlot.excludedDatesLabel}</p>
            )}
            <div className="flex items-center gap-2 text-xs text-[#6B7280] mt-3">
              <MapPin className="h-3.5 w-3.5" /> Venue: {upcomingSlot.venue || "School of Management Building, Mahindra University"}
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <button onClick={onBack} className="mu-btn-secondary" data-testid="btn-back-review">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              className="mu-btn-primary sm:min-w-[240px] disabled:opacity-50"
              data-testid="btn-submit-registration"
              disabled={submitting || isBlocked}
              title={isBlocked ? "Complete all required items to submit" : ""}
            >
              {submitting ? "Submitting…" : isBlocked ? "Complete required items" : "Submit Registration"} <Send className="h-4 w-4" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="dialog-confirm-submit">
            <AlertDialogHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-amber-700" />
                </div>
                <AlertDialogTitle>Confirm submission</AlertDialogTitle>
              </div>
              <AlertDialogDescription asChild>
                <div className="pt-2 space-y-3 text-sm text-[#6B7280]">
                  <p>
                    Once submitted, your registration <span className="font-semibold text-[#B60F2A]">cannot be edited</span>.{" "}
                    {slotOptions?.bookable
                      ? "Your picked reporting slot will be locked in."
                      : isWalkin
                      ? "You can visit any day within the reporting window shown below — no fixed slot, no booking."
                      : "Your reporting slot will be assigned on a first-come, first-serve basis and locked in based on your submission time."}
                  </p>
                  <div className="rounded-xl border border-[#E5E7EB] bg-[#F7F7F7] p-3 text-[#111]">
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold">
                        {slotOptions?.bookable ? "You picked" : isWalkin ? "Reporting window" : "You’ll be allotted"}
                      </span>
                    </div>
                    {slotOptions?.bookable ? (
                      <>
                        <div className="mt-1 font-semibold">
                          {selectedSlot ? `${selectedSlot.dateLabel} · ${selectedSlot.timeLabel}` : "Please select a slot"}
                        </div>
                        <div className="text-xs text-[#6B7280]">
                          {selectedSlot ? `${selectedSlot.venue} — token number issued at check-in` : ""}
                        </div>
                      </>
                    ) : isWalkin ? (
                      <>
                        <div className="mt-1 font-semibold">
                          {upcomingSlot ? `${upcomingSlot.dateLabel} · ${upcomingSlot.timeLabel}` : "Computing…"}
                        </div>
                        <div className="text-xs text-[#6B7280]">
                          {upcomingSlot ? `${upcomingSlot.venue} — token number issued at check-in` : ""}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mt-1 font-semibold">
                          {upcomingSlot ? `${upcomingSlot.dateLabel} · ${upcomingSlot.timeLabel}` : "Computing…"}
                        </div>
                        <div className="text-xs text-[#6B7280]">
                          {upcomingSlot ? `Seat #${upcomingSlot.seatInSlot} · Slot ${upcomingSlot.slotIndex} · ${upcomingSlot.venue}` : ""}
                        </div>
                      </>
                    )}
                  </div>
                  <p className="text-xs">Please review your details carefully before confirming.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="btn-cancel-submit">Review again</AlertDialogCancel>
              <AlertDialogAction
                onClick={submit}
                className="bg-[#B60F2A] hover:bg-[#930C22]"
                data-testid="btn-confirm-submit"
              >
                Yes, submit now
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
